import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Create axios instance with auth header
const apiClient = axios.create({
  baseURL: `${API_URL}/domains`,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add auth token to requests
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('streamToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

class DomainService {
  // Get all custom domains
  async getDomains() {
    try {
      const response = await apiClient.get('/');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Get single domain
  async getDomain(id) {
    try {
      const response = await apiClient.get(`/${id}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Create new custom domain
  async createDomain(domainData) {
    try {
      const response = await apiClient.post('/', domainData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Update custom domain
  async updateDomain(id, domainData) {
    try {
      const response = await apiClient.put(`/${id}`, domainData);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Delete custom domain
  async deleteDomain(id) {
    try {
      const response = await apiClient.delete(`/${id}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Get verification instructions
  async getVerificationInstructions(id, method) {
    try {
      const qs = method ? `?method=${method}` : '';
      const response = await apiClient.get(`/${id}/verification-instructions${qs}`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Verify domain
  async verifyDomain(id, method) {
    try {
      const response = await apiClient.post(`/${id}/verify`, method ? { method } : {});
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Check SSL status
  async checkSSLStatus(id) {
    try {
      const response = await apiClient.get(`/${id}/ssl-status`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Re-issue / repair the SSL certificate for a verified domain
  async provisionSSL(id) {
    try {
      const response = await apiClient.post(`/${id}/provision-ssl`);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Get domain status
  async getStatus() {
    try {
      const response = await apiClient.get('/status');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ── White-label branding ──
  async getBranding() {
    try {
      const response = await apiClient.get('/branding');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async setBrandName(brand_name) {
    try {
      const response = await apiClient.put('/branding', { brand_name });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async uploadLogo(file) {
    try {
      const fd = new FormData();
      fd.append('logo', file);
      const response = await apiClient.post('/branding/logo', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  async removeLogo() {
    try {
      const response = await apiClient.delete('/branding/logo');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // Handle errors
  handleError(error) {
    if (error.response) {
      const message = error.response.data?.error || error.response.data?.message || 'An error occurred';
      const code = error.response.data?.code || 'UNKNOWN_ERROR';
      
      const customError = new Error(message);
      customError.code = code;
      customError.status = error.response.status;
      customError.data = error.response.data;
      
      return customError;
    } else if (error.request) {
      const customError = new Error('No response from server');
      customError.code = 'NO_RESPONSE';
      return customError;
    } else {
      return error;
    }
  }
}

export default new DomainService();
